/**
 * Web storage adapter.
 *
 * localStorage for Supabase session data — persists across tabs and reloads.
 * Supabase tokens are short-lived JWTs refreshed automatically, so localStorage
 * exposure risk is minimal compared to losing session on every new tab.
 *
 * Biometric features are unavailable on web.
 * SSR-safe: guards against Node.js environments where window/localStorage don't exist.
 */

const isBrowser = typeof window !== 'undefined'

/**
 * Supabase-compatible storage adapter.
 * localStorage on web (cross-tab), no-op during SSR.
 */
export const SecureStorageAdapter = {
  getItem: async (key: string): Promise<string | null> => {
    if (!isBrowser) return null
    return localStorage.getItem(key)
  },

  setItem: async (key: string, value: string): Promise<void> => {
    if (!isBrowser) return
    localStorage.setItem(key, value)
  },

  removeItem: async (key: string): Promise<void> => {
    if (!isBrowser) return
    localStorage.removeItem(key)
  },
}

/**
 * Biometric preference storage — unavailable on web.
 */
export const BiometricStore = {
  isEnabled: async (): Promise<boolean> => false,
  setEnabled: async (_enabled: boolean): Promise<void> => {},
  hasBeenPrompted: async (): Promise<boolean> => true,
  setPrompted: async (): Promise<void> => {},
  clear: async (): Promise<void> => {},
}

/** Prefix used by Supabase for session keys in sessionStorage. */
const SUPABASE_STORAGE_PREFIX = 'sb-'

/**
 * Wipe only Supabase auth data on logout.
 * Does NOT nuke the entire sessionStorage (other libs may store data there).
 */
export async function clearAllSecureStorage(): Promise<void> {
  if (!isBrowser) return
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith(SUPABASE_STORAGE_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key)
  }
}
